const RENDER_STROKE_WIDTH = 0.7;
const RENDER_MARKER_STROKE_WIDTH = 0.68;
const RENDER_POINT_RADIUS = 1.1;
const RENDER_LABEL_FONT_SIZE = 4.4;
const DEFAULT_CONTENT_PADDING = 6;
const DEFAULT_RENDER_METRICS = {
  strokeWidth: RENDER_STROKE_WIDTH,
  markerStrokeWidth: RENDER_MARKER_STROKE_WIDTH,
  pointRadius: RENDER_POINT_RADIUS,
  pointLabelFontSize: RENDER_LABEL_FONT_SIZE,
  pointLabelDx: 1.4,
  pointLabelDy: -2.1,
  annotationFontSize: 5,
  leaderStrokeWidth: 0.8
};
const ACCEPTED_RENDER_METRICS = {
  strokeWidth: 1.2,
  markerStrokeWidth: 1.05,
  pointRadius: 2.6,
  pointLabelFontSize: 14,
  pointLabelDx: 6,
  pointLabelDy: -4,
  annotationFontSize: 13,
  leaderStrokeWidth: 1
};

const FIGURE_THEMES = {
  default: {
    shapeStroke: "#1e293b",
    markerStroke: "#1e293b",
    pointFill: "#1e293b",
    pointLabelFill: "#0f172a",
    annotationFill: "#0f172a",
    leaderStroke: "#94a3b8"
  },
  accepted: {
    shapeStroke: "#1d4ed8",
    markerStroke: "#1d4ed8",
    pointFill: "#0f172a",
    pointLabelFill: "#0f172a",
    annotationFill: "#1d4ed8",
    leaderStroke: "#94a3b8"
  }
};

function resolveValue(value, variables) {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    if (variables && Object.prototype.hasOwnProperty.call(variables, value)) {
      const resolved = variables[value];
      return typeof resolved === "number" ? resolved : Number(resolved);
    }
    const numeric = Number(value);
    return Number.isNaN(numeric) ? value : numeric;
  }
  return value;
}

function resolvePointPosition(pos, variables) {
  if (pos && typeof pos === "object" && !Array.isArray(pos)) {
    const x = resolveValue(pos.x, variables);
    const y = resolveValue(pos.y, variables);
    if (typeof x === "number" && typeof y === "number") {
      return { x, y };
    }
  }
  if (!Array.isArray(pos) || pos.length < 2) return null;
  const x = resolveValue(pos[0], variables);
  const y = resolveValue(pos[1], variables);
  if (typeof x !== "number" || typeof y !== "number") return null;
  return { x, y };
}

function buildLineFromPoints(fromPoint, toPoint) {
  if (!fromPoint || !toPoint) return null;
  return {
    x1: fromPoint.x,
    y1: fromPoint.y,
    x2: toPoint.x,
    y2: toPoint.y
  };
}

function intersectLines(lineA, lineB) {
  if (!lineA || !lineB) return null;
  const { x1, y1, x2, y2 } = lineA;
  const { x1: x3, y1: y3, x2: x4, y2: y4 } = lineB;
  const denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
  if (denom === 0) return null;
  const px =
    ((x1 * y2 - y1 * x2) * (x3 - x4) - (x1 - x2) * (x3 * y4 - y3 * x4)) / denom;
  const py =
    ((x1 * y2 - y1 * x2) * (y3 - y4) - (y1 - y2) * (x3 * y4 - y3 * x4)) / denom;
  return { x: px, y: py };
}

function normalizeVector(vector) {
  const magnitude = Math.hypot(Number(vector?.x || 0), Number(vector?.y || 0));
  if (!magnitude) return null;
  return {
    x: Number(vector.x) / magnitude,
    y: Number(vector.y) / magnitude
  };
}

function normalizePositiveRadians(value) {
  const turn = Math.PI * 2;
  let next = Number(value);
  if (!Number.isFinite(next)) return null;
  while (next < 0) next += turn;
  while (next >= turn) next -= turn;
  return next;
}

function resolveArcAngles(startAngle, endAngle) {
  const start = normalizePositiveRadians(startAngle);
  const end = normalizePositiveRadians(endAngle);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
  let sweep = end - start;
  if (Math.abs(sweep) < 0.0001) {
    sweep = Math.PI * 2;
  } else if (sweep <= 0) {
    sweep += Math.PI * 2;
  }
  return {
    startAngle: start,
    endAngle: start + sweep
  };
}

function buildArcPoints(center, radius, startAngle, endAngle, sampleCount = 24) {
  if (!center || !Number.isFinite(radius) || radius <= 0) return [];
  const resolved = resolveArcAngles(startAngle, endAngle);
  if (!resolved) return [];
  const stepCount = Math.max(8, sampleCount);
  const sweep = resolved.endAngle - resolved.startAngle;
  return Array.from({ length: stepCount + 1 }, (_, index) => {
    const t = index / stepCount;
    const angle = resolved.startAngle + sweep * t;
    return [
      Number((center.x + Math.cos(angle) * radius).toFixed(3)),
      Number((center.y + Math.sin(angle) * radius).toFixed(3))
    ];
  });
}

function resolveSegmentAnnotationPosition(annotation, lineMap, variables) {
  const ref = String(
    annotation.segment || annotation.line || annotation.ref || annotation.targetRef || ""
  ).trim();
  const line = ref ? lineMap?.[ref] : null;
  if (!line) return null;
  const midpoint = {
    x: (line.x1 + line.x2) / 2,
    y: (line.y1 + line.y2) / 2
  };
  const dx = line.x2 - line.x1;
  const dy = line.y2 - line.y1;
  const length = Math.hypot(dx, dy) || 1;
  const defaultOffset = {
    x: (-dy / length) * 4,
    y: (dx / length) * 4
  };
  const offset = resolvePointPosition(annotation.offset, variables) || defaultOffset;
  return {
    x: midpoint.x + offset.x,
    y: midpoint.y + offset.y
  };
}

function resolveAngleAnnotationPosition(annotation, points, variables) {
  const refs = Array.isArray(annotation.refs || annotation.anglePoints || annotation.points)
    ? annotation.refs || annotation.anglePoints || annotation.points
    : [];
  if (refs.length < 3) return null;
  const pointA = points?.[refs[0]];
  const vertex = points?.[refs[1]];
  const pointB = points?.[refs[2]];
  if (!pointA || !vertex || !pointB) return null;

  const vectorA = normalizeVector({
    x: pointA.x - vertex.x,
    y: pointA.y - vertex.y
  });
  const vectorB = normalizeVector({
    x: pointB.x - vertex.x,
    y: pointB.y - vertex.y
  });
  if (!vectorA || !vectorB) return null;

  let bisector = normalizeVector({
    x: vectorA.x + vectorB.x,
    y: vectorA.y + vectorB.y
  });
  if (!bisector) {
    bisector = normalizeVector({
      x: vectorA.y,
      y: -vectorA.x
    });
  }
  if (!bisector) return null;

  const baseRadius =
    typeof resolveValue(annotation.radius, variables) === "number"
      ? resolveValue(annotation.radius, variables)
      : Math.max(
          6,
          Math.min(
            14,
            Math.min(
              Math.hypot(pointA.x - vertex.x, pointA.y - vertex.y),
              Math.hypot(pointB.x - vertex.x, pointB.y - vertex.y)
            ) * 0.32
          )
        );
  const offset = resolvePointPosition(annotation.offset, variables) || { x: 0, y: 0 };
  return {
    x: vertex.x + bisector.x * baseRadius + offset.x,
    y: vertex.y + bisector.y * baseRadius + offset.y
  };
}

function resolveAnnotation(annotation, points, lineMap, variables, index) {
  if (!annotation || typeof annotation !== "object") return null;
  const id = annotation.id || `annotation_${index + 1}`;
  const rawText = String(annotation.text || annotation.label || "").trim();
  const text =
    /\{\{[^}]+\}\}/.test(rawText) && annotation.displayText
      ? String(annotation.displayText || "").trim()
      : rawText;
  if (!text) return null;

  let x = null;
  let y = null;
  const pos = resolvePointPosition(annotation.pos || annotation.position, variables);
  if (pos) {
    x = pos.x;
    y = pos.y;
  } else if (annotation.point && points[annotation.point]) {
    const base = points[annotation.point];
    const offset = resolvePointPosition(annotation.offset || [4, -4], variables) || { x: 4, y: -4 };
    x = base.x + offset.x;
    y = base.y + offset.y;
  } else if (String(annotation.targetType || "").trim().toLowerCase() === "segment") {
    const anchor = resolveSegmentAnnotationPosition(annotation, lineMap, variables);
    if (anchor) {
      x = anchor.x;
      y = anchor.y;
    }
  } else if (String(annotation.targetType || "").trim().toLowerCase() === "angle") {
    const anchor = resolveAngleAnnotationPosition(annotation, points, variables);
    if (anchor) {
      x = anchor.x;
      y = anchor.y;
    }
  }

  if (typeof x !== "number" || typeof y !== "number") return null;

  return {
    id,
    type: "text",
    x,
    y,
    anchorX: (() => {
      const anchor = resolvePointPosition(annotation.anchorPos || annotation.anchorPosition || annotation.anchor, variables);
      return anchor ? anchor.x : null;
    })(),
    anchorY: (() => {
      const anchor = resolvePointPosition(annotation.anchorPos || annotation.anchorPosition || annotation.anchor, variables);
      return anchor ? anchor.y : null;
    })(),
    text,
    fontSize:
      typeof resolveValue(annotation.fontSize, variables) === "number"
        ? resolveValue(annotation.fontSize, variables)
        : 5
  };
}

function materializeFigureSpec(spec, variables = {}) {
  if (!spec || typeof spec !== "object") return null;
  if (!spec.canvas || !spec.entities) return spec;

  const canvas = spec.canvas || { viewBox: "0 0 100 100", responsive: true };
  const entities = spec.entities || {};
  const pointsDef = Array.isArray(entities.points) ? entities.points : [];
  const linesDef = Array.isArray(entities.lines) ? entities.lines : [];
  const shapesDef = Array.isArray(entities.shapes) ? entities.shapes : [];
  const annotationsDef = Array.isArray(entities.annotations) ? entities.annotations : [];

  const points = {};
  const unresolved = new Set(pointsDef.map((pt) => pt.id).filter(Boolean));

  pointsDef.forEach((pt) => {
    if (pt?.type !== "fixed") return;
    const pos = resolvePointPosition(pt.pos, variables);
    if (!pos || !pt.id) return;
    points[pt.id] = { ...pos, label: pt.label || pt.id, displayLabel: pt.displayLabel || null };
    unresolved.delete(pt.id);
  });

  let guard = 0;
  while (unresolved.size > 0 && guard < pointsDef.length + 5) {
    guard += 1;
    pointsDef.forEach((pt) => {
      if (!pt?.id || !unresolved.has(pt.id)) return;
      if (pt.type === "relative") {
        const base = points[pt.base];
        const offset = resolvePointPosition(pt.offset, variables);
        if (base && offset) {
          points[pt.id] = {
            x: base.x + offset.x,
            y: base.y + offset.y,
            label: pt.label || pt.id,
            displayLabel: pt.displayLabel || null
          };
          unresolved.delete(pt.id);
        }
      }
      if (pt.type === "midpoint") {
        const [a, b] = Array.isArray(pt.points) ? pt.points : [];
        const pa = points[a];
        const pb = points[b];
        if (pa && pb) {
          points[pt.id] = {
            x: (pa.x + pb.x) / 2,
            y: (pa.y + pb.y) / 2,
            label: pt.label || pt.id,
            displayLabel: pt.displayLabel || null
          };
          unresolved.delete(pt.id);
        }
      }
    });
  }

  const lineMap = {};
  const lines = linesDef
    .map((line) => {
      const fromPoint = points[line.from];
      const toPoint = points[line.to];
      const segment = buildLineFromPoints(fromPoint, toPoint);
      if (!segment) return null;
      const entry = {
        id: line.id,
        type: line.type || "segment",
        color: String(line.color || "").trim() || null,
        ...segment
      };
      if (line.dash) entry.dash = line.dash;
      lineMap[line.id] = entry;
      return entry;
    })
    .filter(Boolean);

  pointsDef.forEach((pt) => {
    if (!pt?.id || !unresolved.has(pt.id)) return;
    if (pt.type !== "intersection") return;
    const [a, b] = Array.isArray(pt.refs) ? pt.refs : [];
    const lineA = lineMap[a];
    const lineB = lineMap[b];
    const intersection = intersectLines(lineA, lineB);
    if (!intersection) return;
    points[pt.id] = { ...intersection, label: pt.label || pt.id, displayLabel: pt.displayLabel || null };
    unresolved.delete(pt.id);
  });

  const shapes = shapesDef
    .map((shape) => {
      if (shape.type === "circle") {
        const center = points[shape.center];
        const radius = resolveValue(shape.r ?? shape.radius, variables);
        if (!center || typeof radius !== "number") return null;
        return {
          id: shape.id,
          type: "circle",
          cx: center.x,
          cy: center.y,
          r: radius
        };
      }
      if (shape.type === "arc") {
        const center =
          resolvePointPosition(shape.centerPos, variables) ||
          points[shape.center] ||
          resolvePointPosition(shape.center, variables);
        const radius = resolveValue(shape.r ?? shape.radius, variables);
        const startAngle = resolveValue(shape.startAngle, variables);
        const endAngle = resolveValue(shape.endAngle, variables);
        if (!center || typeof radius !== "number" || typeof startAngle !== "number" || typeof endAngle !== "number") {
          return null;
        }
        const arcPoints = buildArcPoints(center, radius, startAngle, endAngle);
        if (arcPoints.length < 2) return null;
        return {
          id: shape.id,
          type: "arc",
          cx: center.x,
          cy: center.y,
          r: radius,
          startAngle,
          endAngle,
          points: arcPoints
        };
      }
      if (shape.type === "polygon") {
        const pts = (Array.isArray(shape.points) ? shape.points : []).map((id) => points[id]).filter(Boolean);
        if (pts.length < 3) return null;
        return {
          id: shape.id,
          type: "polygon",
          points: pts.map((pt) => [pt.x, pt.y])
        };
      }
      if (shape.type === "polyline") {
        const pts = (Array.isArray(shape.points) ? shape.points : [])
          .map((pt) => resolvePointPosition(pt, variables))
          .filter(Boolean);
        if (pts.length < 2) return null;
        return {
          id: shape.id,
          type: "polyline",
          points: pts.map((pt) => [pt.x, pt.y])
        };
      }
      return null;
    })
    .filter(Boolean);

  const annotations = annotationsDef
    .map((annotation, index) => resolveAnnotation(annotation, points, lineMap, variables, index))
    .filter(Boolean);

  return {
    canvas,
    points,
    lines,
    shapes,
    annotations
  };
}

function denormalizeRenderSpecToStage(spec) {
  const stageWidth = Number(spec?.canvas?.normalizedStageWidth || 0);
  const stageHeight = Number(spec?.canvas?.normalizedStageHeight || 0);
  const viewBox = String(spec?.canvas?.viewBox || "").trim();
  if (
    !Number.isFinite(stageWidth) ||
    !Number.isFinite(stageHeight) ||
    stageWidth <= 0 ||
    stageHeight <= 0 ||
    viewBox !== "0 0 100 100"
  ) {
    return spec;
  }

  const scaleX = stageWidth / 100;
  const scaleY = stageHeight / 100;
  const scaleRadius = Math.min(stageWidth, stageHeight) / 100;
  const scalePoint = (point) => ({
    ...point,
    x: Number((Number(point?.x || 0) * scaleX).toFixed(3)),
    y: Number((Number(point?.y || 0) * scaleY).toFixed(3))
  });
  const scalePointTuple = (point) => [
    Number((Number(point?.[0] || 0) * scaleX).toFixed(3)),
    Number((Number(point?.[1] || 0) * scaleY).toFixed(3))
  ];

  return {
    ...spec,
    canvas: {
      ...(spec.canvas || {}),
      viewBox: `0 0 ${Number(stageWidth.toFixed(3))} ${Number(stageHeight.toFixed(3))}`
    },
    points: Object.fromEntries(
      Object.entries(spec.points || {}).map(([id, point]) => [id, scalePoint(point)])
    ),
    lines: (Array.isArray(spec.lines) ? spec.lines : []).map((line) => ({
      ...line,
      x1: Number((Number(line?.x1 || 0) * scaleX).toFixed(3)),
      y1: Number((Number(line?.y1 || 0) * scaleY).toFixed(3)),
      x2: Number((Number(line?.x2 || 0) * scaleX).toFixed(3)),
      y2: Number((Number(line?.y2 || 0) * scaleY).toFixed(3))
    })),
    shapes: (Array.isArray(spec.shapes) ? spec.shapes : []).map((shape) => {
      if (shape?.type === "circle") {
        return {
          ...shape,
          cx: Number((Number(shape?.cx || 0) * scaleX).toFixed(3)),
          cy: Number((Number(shape?.cy || 0) * scaleY).toFixed(3)),
          r: Number((Number(shape?.r || 0) * scaleRadius).toFixed(3))
        };
      }
      if (Array.isArray(shape?.points)) {
        return {
          ...shape,
          points: shape.points.map(scalePointTuple)
        };
      }
      return shape;
    }),
    annotations: (Array.isArray(spec.annotations) ? spec.annotations : []).map((annotation) => ({
      ...annotation,
      x: Number((Number(annotation?.x || 0) * scaleX).toFixed(3)),
      y: Number((Number(annotation?.y || 0) * scaleY).toFixed(3)),
      anchorX:
        typeof annotation?.anchorX === "number"
          ? Number((annotation.anchorX * scaleX).toFixed(3))
          : annotation?.anchorX,
      anchorY:
        typeof annotation?.anchorY === "number"
          ? Number((annotation.anchorY * scaleY).toFixed(3))
          : annotation?.anchorY
    }))
  };
}

function renderFigureFromRenderSpec(spec, themeName = "default") {
  if (!spec) return null;
  const viewBox = spec.canvas?.viewBox || "0 0 100 100";
  const points = spec.points || {};
  const lines = spec.lines || [];
  const shapes = spec.shapes || [];
  const annotations = spec.annotations || [];
  const theme = FIGURE_THEMES[themeName] || FIGURE_THEMES.default;
  const metrics = resolveRenderMetrics(themeName, viewBox);

  return (
    <svg viewBox={viewBox} className="figure-svg" aria-label="Geometry diagram">
      {shapes.map((shape) => {
        if (shape.type === "circle") {
          return (
            <circle
              key={shape.id}
              cx={shape.cx}
              cy={shape.cy}
              r={shape.r}
              fill="none"
              stroke={theme.shapeStroke}
              strokeWidth={metrics.strokeWidth}
            />
          );
        }
        if (shape.type === "polygon") {
          const pts = (shape.points || []).map((pt) => pt.join(",")).join(" ");
          return (
            <polygon
              key={shape.id}
              points={pts}
              fill="none"
              stroke={theme.shapeStroke}
              strokeWidth={metrics.strokeWidth}
            />
          );
        }
        if (shape.type === "arc") {
          const pts = (shape.points || []).map((pt) => pt.join(",")).join(" ");
          return (
            <polyline
              key={shape.id}
              points={pts}
              fill="none"
              stroke={theme.shapeStroke}
              strokeWidth={metrics.strokeWidth}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          );
        }
        if (shape.type === "polyline") {
          const pts = (shape.points || []).map((pt) => pt.join(",")).join(" ");
          return (
            <polyline
              key={shape.id}
              points={pts}
              fill="none"
              stroke={theme.markerStroke}
              strokeWidth={metrics.markerStrokeWidth}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          );
        }
        return null;
      })}
      {lines.map((line) => (
        <line
          key={line.id}
          x1={line.x1}
          y1={line.y1}
          x2={line.x2}
          y2={line.y2}
          stroke={line.color || theme.shapeStroke}
          strokeWidth={metrics.strokeWidth}
          strokeDasharray={line.dash ? line.dash.join(" ") : undefined}
        />
      ))}
      {Object.entries(points).map(([id, pt]) => (
        <g key={id}>
          <circle cx={pt.x} cy={pt.y} r={metrics.pointRadius} fill={theme.pointFill} />
          <text
            x={pt.x + metrics.pointLabelDx}
            y={pt.y + metrics.pointLabelDy}
            fontSize={metrics.pointLabelFontSize}
            fill={theme.pointLabelFill}
          >
            {(/\{\{[^}]+\}\}/.test(String(pt.label || "")) && pt.displayLabel) ? pt.displayLabel : pt.label || id}
          </text>
        </g>
      ))}
      {annotations.map((annotation) => {
        const rawFontSize = Number(annotation.fontSize);
        const fontSize =
          Number.isFinite(rawFontSize) && rawFontSize > 5.1
            ? rawFontSize
            : metrics.annotationFontSize;
        const textWidth = Math.max(fontSize * 0.7, String(annotation.text || "").length * fontSize * 0.58);
        const rect = {
          x: annotation.x,
          y: annotation.y - fontSize,
          width: textWidth,
          height: fontSize + 4
        };
        const leaderPoints =
          typeof annotation.anchorX === "number" && typeof annotation.anchorY === "number"
            ? [
                annotation.anchorX,
                annotation.anchorY,
                Math.min(Math.max(annotation.anchorX, rect.x + 2), rect.x + rect.width - 2),
                Math.min(Math.max(annotation.anchorY, rect.y + 2), rect.y + rect.height - 2)
              ]
            : null;
        const showLeader =
          leaderPoints &&
          Math.hypot(leaderPoints[0] - leaderPoints[2], leaderPoints[1] - leaderPoints[3]) >= 10;
        return (
          <g key={annotation.id}>
            {showLeader && (
              <line
                x1={leaderPoints[0]}
                y1={leaderPoints[1]}
                x2={leaderPoints[2]}
                y2={leaderPoints[3]}
                stroke={theme.leaderStroke}
                strokeWidth={metrics.leaderStrokeWidth}
                strokeDasharray="2.5 2.5"
              />
            )}
            <text
              x={annotation.x}
              y={annotation.y}
              fontSize={fontSize}
              fill={theme.annotationFill}
            >
              {annotation.text}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function parseViewBox(viewBox) {
  const parts = String(viewBox || "")
    .trim()
    .split(/\s+/)
    .map((value) => Number(value));
  if (parts.length !== 4 || parts.some((value) => !Number.isFinite(value))) {
    return { minX: 0, minY: 0, width: 100, height: 100 };
  }
  return {
    minX: parts[0],
    minY: parts[1],
    width: parts[2],
    height: parts[3]
  };
}

function resolveRenderMetrics(themeName, viewBox) {
  const parsed = parseViewBox(viewBox);
  const isStageSizedRender = Math.max(parsed.width, parsed.height) > 160;
  if (themeName === "accepted" && isStageSizedRender) {
    return ACCEPTED_RENDER_METRICS;
  }
  return DEFAULT_RENDER_METRICS;
}

function expandBounds(bounds, x, y) {
  if (!Number.isFinite(x) || !Number.isFinite(y)) return bounds;
  if (!bounds) {
    return {
      minX: x,
      minY: y,
      maxX: x,
      maxY: y
    };
  }
  return {
    minX: Math.min(bounds.minX, x),
    minY: Math.min(bounds.minY, y),
    maxX: Math.max(bounds.maxX, x),
    maxY: Math.max(bounds.maxY, y)
  };
}

function measureRenderSpecBounds(spec, themeName = "default") {
  if (!spec || typeof spec !== "object") return null;
  let bounds = null;
  const metrics = resolveRenderMetrics(themeName, spec?.canvas?.viewBox || "0 0 100 100");

  Object.values(spec.points || {}).forEach((point) => {
    bounds = expandBounds(bounds, Number(point?.x), Number(point?.y));
  });

  (Array.isArray(spec.lines) ? spec.lines : []).forEach((line) => {
    bounds = expandBounds(bounds, Number(line?.x1), Number(line?.y1));
    bounds = expandBounds(bounds, Number(line?.x2), Number(line?.y2));
  });

  (Array.isArray(spec.shapes) ? spec.shapes : []).forEach((shape) => {
    if (shape?.type === "circle") {
      const cx = Number(shape?.cx);
      const cy = Number(shape?.cy);
      const r = Number(shape?.r);
      bounds = expandBounds(bounds, cx - r, cy - r);
      bounds = expandBounds(bounds, cx + r, cy + r);
      return;
    }
    if (shape?.type === "polygon" || shape?.type === "polyline" || shape?.type === "arc") {
      (Array.isArray(shape?.points) ? shape.points : []).forEach((point) => {
        if (!Array.isArray(point) || point.length < 2) return;
        bounds = expandBounds(bounds, Number(point[0]), Number(point[1]));
      });
    }
  });

  (Array.isArray(spec.annotations) ? spec.annotations : []).forEach((annotation) => {
    const x = Number(annotation?.x);
    const y = Number(annotation?.y);
    const rawFontSize = Number(annotation?.fontSize);
    const fontSize =
      Number.isFinite(rawFontSize) && rawFontSize > 5.1
        ? rawFontSize
        : metrics.annotationFontSize;
    const text = String(annotation?.text || "");
    const estimatedWidth = Math.max(fontSize * 0.7, text.length * fontSize * 0.58);
    bounds = expandBounds(bounds, x, y - fontSize);
    bounds = expandBounds(bounds, x + estimatedWidth, y + fontSize * 0.25);
    if (Number.isFinite(Number(annotation?.anchorX)) && Number.isFinite(Number(annotation?.anchorY))) {
      bounds = expandBounds(bounds, Number(annotation.anchorX), Number(annotation.anchorY));
    }
  });

  Object.values(spec.points || {}).forEach((point) => {
    const x = Number(point?.x);
    const y = Number(point?.y);
    const label = String(point?.displayLabel || point?.label || "");
    if (!label) return;
    const estimatedWidth = Math.max(
      metrics.pointLabelFontSize * 0.7,
      label.length * metrics.pointLabelFontSize * 0.58
    );
    bounds = expandBounds(
      bounds,
      x + metrics.pointLabelDx,
      y + metrics.pointLabelDy - metrics.pointLabelFontSize
    );
    bounds = expandBounds(
      bounds,
      x + metrics.pointLabelDx + estimatedWidth,
      y + metrics.pointLabelDy + metrics.pointLabelFontSize * 0.25
    );
  });

  return bounds;
}

function buildCroppedViewBox(spec, padding = DEFAULT_CONTENT_PADDING, themeName = "default") {
  const fallback = parseViewBox(spec?.canvas?.viewBox);
  const bounds = measureRenderSpecBounds(spec, themeName);
  if (!bounds) return `${fallback.minX} ${fallback.minY} ${fallback.width} ${fallback.height}`;

  const pad = Math.max(0, Number(padding) || 0);
  const minX = bounds.minX - pad;
  const minY = bounds.minY - pad;
  const width = Math.max(12, bounds.maxX - bounds.minX + pad * 2);
  const height = Math.max(12, bounds.maxY - bounds.minY + pad * 2);
  return `${Number(minX.toFixed(2))} ${Number(minY.toFixed(2))} ${Number(width.toFixed(2))} ${Number(height.toFixed(2))}`;
}

function normalizePoint(point) {
  if (!Array.isArray(point) || point.length < 2) return { x: 50, y: 50 };
  const [x, y] = point;
  return { x: Math.round(x * 100), y: Math.round(y * 100) };
}

function renderLegacyFigure(spec) {
  if (!spec || !spec.type) return null;
  const width = 100;
  const height = 100;

  if (spec.type === "triangle") {
    const points = spec.points || {};
    const a = normalizePoint(points.A);
    const b = normalizePoint(points.B);
    const c = normalizePoint(points.C);
    const polygon = `${a.x},${a.y} ${b.x},${b.y} ${c.x},${c.y}`;
    const labels = spec.labels || {};

    return (
        <svg viewBox={`0 0 ${width} ${height}`} className="figure-svg" aria-label="Triangle diagram">
          <polygon points={polygon} fill="none" stroke="#1e293b" strokeWidth={RENDER_STROKE_WIDTH} />
          {[{ id: "A", p: a }, { id: "B", p: b }, { id: "C", p: c }].map((item) => (
            <g key={item.id}>
              <circle cx={item.p.x} cy={item.p.y} r={RENDER_POINT_RADIUS} fill="#1e293b" />
              <text x={item.p.x + 1.4} y={item.p.y - 2.1} fontSize={RENDER_LABEL_FONT_SIZE} fill="#0f172a">
                {labels[item.id] || item.id}
              </text>
            </g>
        ))}
      </svg>
    );
  }

  if (spec.type === "segment") {
    const points = spec.points || {};
    const a = normalizePoint(points.A);
    const b = normalizePoint(points.B);
    const labels = spec.labels || {};

    return (
        <svg viewBox={`0 0 ${width} ${height}`} className="figure-svg" aria-label="Line segment diagram">
          <line x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke="#1e293b" strokeWidth={RENDER_STROKE_WIDTH} />
          {[{ id: "A", p: a }, { id: "B", p: b }].map((item) => (
            <g key={item.id}>
              <circle cx={item.p.x} cy={item.p.y} r={RENDER_POINT_RADIUS} fill="#1e293b" />
              <text x={item.p.x + 1.4} y={item.p.y - 2.1} fontSize={RENDER_LABEL_FONT_SIZE} fill="#0f172a">
                {labels[item.id] || item.id}
              </text>
            </g>
        ))}
      </svg>
    );
  }

  return null;
}

export function FigureRender({
  figureSpec,
  variables = {},
  cropToContent = false,
  contentPadding = DEFAULT_CONTENT_PADDING,
  theme = "default"
}) {
  if (!figureSpec) return null;
  if (figureSpec.canvas) {
    const renderSpec = denormalizeRenderSpecToStage(materializeFigureSpec(figureSpec, variables));
    if (!renderSpec) return null;
    if (cropToContent) {
      const croppedViewBox = buildCroppedViewBox(renderSpec, contentPadding, theme);
      return renderFigureFromRenderSpec({
        ...renderSpec,
        canvas: {
          ...(renderSpec.canvas || {}),
          viewBox: croppedViewBox
        }
      }, theme);
    }
    return renderFigureFromRenderSpec(renderSpec, theme);
  }
  return renderLegacyFigure(figureSpec);
}
